import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Car, Search, RefreshCw, Phone, User, Calendar, Building2,
  ArrowRightLeft, UserX, CheckCircle, XCircle, Clock
} from "lucide-react";

export const AllDriversPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [adminFilter, setAdminFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Reassign modal
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [newAdminId, setNewAdminId] = useState("");
  const [reassigning, setReassigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [driversRes, adminsRes] = await Promise.all([
        axios.get(`${API}/superadmin/all-drivers`),
        axios.get(`${API}/superadmin/admins`)
      ]);
      setDrivers(driversRes.data.drivers || driversRes.data || []);
      setAdmins(adminsRes.data || []);
    } catch (err) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedDriver) return;
    
    setReassigning(true);
    try {
      await axios.put(`${API}/superadmin/drivers/${selectedDriver.id}/reassign`, {
        new_admin_id: newAdminId || null
      });
      toast.success(newAdminId ? "Driver reassigned successfully" : "Driver unassigned successfully");
      setShowReassignModal(false);
      setSelectedDriver(null);
      setNewAdminId("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reassign driver");
    } finally {
      setReassigning(false);
    }
  };

  const openReassignModal = (driver) => {
    setSelectedDriver(driver);
    setNewAdminId(driver.owner_admin_id || "");
    setShowReassignModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      rejected: "bg-red-100 text-red-700",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status?.toLowerCase()] || "bg-gray-100 text-gray-700"}`}>
        {status || "Unknown"}
      </span>
    );
  };

  const getAdminName = (adminId) => {
    if (!adminId) return "Unassigned";
    const admin = admins.find(a => a.id === adminId);
    return admin?.name || "Unknown Admin";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  // Filter drivers
  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = 
      driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.phone?.includes(searchTerm) ||
      driver.vehicle_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAdmin = adminFilter === "all" || 
      (adminFilter === "unassigned" ? !driver.owner_admin_id : driver.owner_admin_id === adminFilter);
    
    const matchesStatus = statusFilter === "all" || 
      driver.approval_status?.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesAdmin && matchesStatus;
  });

  return (
    <div data-testid="all-drivers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">All Drivers</h1>
          <p className="text-muted-foreground">Manage and reassign drivers across admins</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search name, phone, vehicle..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        <Select value={adminFilter} onValueChange={setAdminFilter}>
          <SelectTrigger data-testid="admin-filter">
            <SelectValue placeholder="Filter by Admin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Admins</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {admins.map(admin => (
              <SelectItem key={admin.id} value={admin.id}>{admin.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="status-filter">
            <SelectValue placeholder="Filter by Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        
        <div className="flex items-center justify-end text-sm text-muted-foreground">
          Showing {filteredDrivers.length} of {drivers.length} drivers
        </div>
      </div>

      {/* Drivers Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredDrivers.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <Car className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No drivers found</p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>App Ver</TableHead>
                <TableHead>Assigned Admin</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDrivers.map((driver) => (
                <TableRow key={driver.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{driver.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {driver.phone}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium capitalize">{driver.vehicle_type || "N/A"}</p>
                      <p className="text-xs text-muted-foreground">{driver.vehicle_number || "No vehicle"}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {driver.app_version ? (
                      <span className={`px-2 py-1 rounded text-xs font-mono ${
                        driver.app_version === '1.0.11' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        v{driver.app_version}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className={driver.owner_admin_id ? "" : "text-muted-foreground italic"}>
                        {getAdminName(driver.owner_admin_id)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(driver.approval_status)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(driver.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openReassignModal(driver)}
                      data-testid={`reassign-${driver.id}`}
                    >
                      <ArrowRightLeft className="w-4 h-4 mr-1" />
                      Reassign
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Reassign Modal */}
      <Dialog open={showReassignModal} onOpenChange={setShowReassignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Reassign Driver
            </DialogTitle>
            <DialogDescription>
              Change the admin assignment for this driver
            </DialogDescription>
          </DialogHeader>
          
          {selectedDriver && (
            <div className="space-y-4 py-4">
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-sm"><strong>Driver:</strong> {selectedDriver.name}</p>
                <p className="text-sm"><strong>Phone:</strong> {selectedDriver.phone}</p>
                <p className="text-sm"><strong>Current Admin:</strong> {getAdminName(selectedDriver.owner_admin_id)}</p>
              </div>
              
              <div>
                <Label>Assign to Admin</Label>
                <Select value={newAdminId || "__unassign__"} onValueChange={(val) => setNewAdminId(val === "__unassign__" ? "" : val)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Admin (or leave empty to unassign)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassign__">
                      <span className="flex items-center gap-2">
                        <UserX className="w-4 h-4" />
                        Unassign (No Admin)
                      </span>
                    </SelectItem>
                    {admins.map(admin => (
                      <SelectItem key={admin.id} value={admin.id}>
                        <span className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {admin.name} ({admin.phone})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleReassign} disabled={reassigning}>
              {reassigning ? "Reassigning..." : newAdminId ? "Reassign Driver" : "Unassign Driver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllDriversPage;
